import { i18n } from '@/locales';

/**
 * Backend domain errors expose a stable, machine-readable shape:
 *
 *   {
 *     code: 'CONFLICT' | 'NOT_FOUND' | 'VALIDATION_ERROR' | …,
 *     message: 'Human-readable but English-only',
 *     errors?: {
 *       conflictingResource?: { entity, field, value },
 *       …
 *     }
 *   }
 *
 * `translateApiError` keys off `code` (and the structured payload inside
 * `errors`) to produce a localized message in the active locale. The
 * backend's English `message` is never shown: it can name record ids, and
 * a failure no mapping fits gets the caller's message or a generic one.
 *
 * Single point of i18n for API errors: when a new domain error lands on
 * the backend, add the matching key under `errors.api.*` in en/es/pt and
 * extend the switch below. Callers (services via `mapApiError`) stay
 * untouched.
 */

interface ApiErrorPayload {
  code?: string;
  message?: string;
  errors?: {
    conflictingResource?: { entity?: string; field?: string; value?: unknown };
    rule?: unknown;
    [key: string]: unknown;
  };
}

interface AxiosLikeError {
  isAxiosError?: boolean;
  response?: { status?: number; data?: ApiErrorPayload | string | null };
  message?: string;
}

const SERVER_ERROR_MIN_STATUS = 500;

const HttpStatus = { FORBIDDEN: 403, NOT_FOUND: 404, CONFLICT: 409, PAYLOAD_TOO_LARGE: 413, TOO_MANY_REQUESTS: 429 } as const;

const UNEXPECTED_KEY = 'errors.api.unexpected';

/** The i18n keys of the failures `apiErrorKey` recognises whatever the flow. */
export const ApiErrorKey = {
  NETWORK: 'errors.api.network',
  ACCESS_DENIED: 'errors.api.accessDenied',
  ITEM_UNAVAILABLE: 'errors.api.itemUnavailable',
  PRESENTATION_MEDICATION_MISMATCH: 'errors.api.presentationMedicationMismatch',
  PATIENT_REFERENCE_MISMATCH: 'errors.api.patientReferenceMismatch',
  TOO_MANY_REQUESTS: 'errors.api.tooManyRequests',
  CONFLICT: 'errors.api.conflict.generic',
  PAYLOAD_TOO_LARGE: 'errors.api.payloadTooLarge',
} as const;

// An unknown id and another organization's id get the same answer.
const NOT_FOUND_CODES: readonly string[] = ['NOT_FOUND', 'ENTITY_NOT_FOUND', 'RESOURCE_NOT_FOUND'];

/**
 * Record names a 404 carries in `errors.entity`. An unknown record and another organization's get the same 404, so a
 * flow can say which of the records it sent is missing (the hospital a patient picked, say) without telling the two apart.
 */
export const ApiEntity = {
  HOSPITAL: 'Hospital',
} as const;

// A record that exists but does not fit the request.
const MISMATCH_KEYS: Readonly<Record<string, string>> = {
  PRESENTATION_MEDICATION_MISMATCH: ApiErrorKey.PRESENTATION_MEDICATION_MISMATCH,
  PATIENT_REFERENCE_MISMATCH: ApiErrorKey.PATIENT_REFERENCE_MISMATCH,
};

// Codes come from the API, so names like "constructor" must not reach Object.prototype.
const ownKey = (keys: Readonly<Record<string, string>>, name: string): string | undefined =>
  Object.prototype.hasOwnProperty.call(keys, name) ? keys[name] : undefined;

const ENTITY_KEY: Record<string, string> = {
  Patient: 'errors.api.entity.patient',
  Appointment: 'errors.api.entity.appointment',
  Invoice: 'errors.api.entity.invoice',
  Encounter: 'errors.api.entity.encounter',
  User: 'errors.api.entity.user',
  Hospital: 'errors.api.entity.hospital',
  Organization: 'errors.api.entity.organization',
  Doctor: 'errors.api.entity.doctor',
  Nurse: 'errors.api.entity.nurse',
};

const FIELD_KEY: Record<string, string> = {
  'email': 'errors.api.field.email',
  'phone': 'errors.api.field.phone',
  'nationalId': 'errors.api.field.nationalId',
  'phone/email/nationalId': 'errors.api.field.contact',
};

function tr(key: string, named?: Record<string, unknown>): string {
  const t = i18n.global.t as (key: string, named?: Record<string, unknown>) => string;
  return named ? t(key, named) : t(key);
}

// The translated label of a known entity or field name, or undefined.
const knownLabel = (keys: Record<string, string>, name: string): string | undefined => {
  const key = ownKey(keys, name);
  return key ? tr(key) : undefined;
};

/** True when the request never got a response (no connection, aborted), as opposed to an error the server answered with. */
export function isConnectionError(err: unknown): boolean {
  const requestError = err as AxiosLikeError | undefined;
  return Boolean(requestError?.isAxiosError && !requestError.response);
}

/**
 * The translated message of a failed request. Known domain codes get their own message; anything else gets
 * `fallbackKey` when the caller names one, else a generic message. Axios's own texts ("Network Error",
 * "Request failed with status code 503") are not shown either.
 */
export function translateApiError(err: AxiosLikeError | unknown, fallbackKey?: string): string {
  const requestError = err as AxiosLikeError | undefined;
  if (isConnectionError(err)) {
    return tr(ApiErrorKey.NETWORK);
  }
  const status = requestError?.response?.status ?? 0;
  if (status === HttpStatus.TOO_MANY_REQUESTS) {
    return tr(ApiErrorKey.TOO_MANY_REQUESTS);
  }
  if (status === HttpStatus.PAYLOAD_TOO_LARGE) {
    return tr(ApiErrorKey.PAYLOAD_TOO_LARGE);
  }
  const payload = extractPayload(err);
  const domainMessage = payload?.code ? translateDomainError(payload.code, payload) : null;
  if (domainMessage !== null) return domainMessage;
  if (fallbackKey) return tr(fallbackKey);
  return tr(status >= SERVER_ERROR_MIN_STATUS ? 'errors.api.internal' : UNEXPECTED_KEY);
}

// The message of a known domain error code, or null when the code is not one of them.
function translateDomainError(code: string, payload: ApiErrorPayload): string | null {
  switch (code) {
    case 'CONFLICT': {
      const c = payload.errors?.conflictingResource;
      const entityLabel = c?.entity ? knownLabel(ENTITY_KEY, c.entity) : undefined;
      const fieldLabel = c?.field ? knownLabel(FIELD_KEY, c.field) : undefined;
      if (entityLabel && fieldLabel) {
        return tr('errors.api.conflict.alreadyExists', { entity: entityLabel, field: fieldLabel, value: String(c?.value ?? '') });
      }
      return tr('errors.api.conflict.generic');
    }
    case 'NOT_FOUND':
    case 'ENTITY_NOT_FOUND':
    case 'RESOURCE_NOT_FOUND':
      return tr('errors.api.notFound');
    case 'PRESENTATION_MEDICATION_MISMATCH':
      return tr(ApiErrorKey.PRESENTATION_MEDICATION_MISMATCH);
    case 'PATIENT_REFERENCE_MISMATCH':
      return tr(ApiErrorKey.PATIENT_REFERENCE_MISMATCH);
    // Class-validator failures arrive as BAD_REQUEST with the per-field detail in English.
    case 'VALIDATION_ERROR':
    case 'REQUIRED_FIELD':
    case 'INVALID_FORMAT':
    case 'BAD_REQUEST':
      return tr('errors.api.validation');
    case 'BUSINESS_RULE_VIOLATION':
      return tr('errors.api.businessRule');
    case 'INVALID_CREDENTIALS':
      return tr('errors.api.invalidCredentials');
    case 'INACTIVE_ACCOUNT':
      return tr('errors.api.inactiveAccount');
    case 'INVALID_TOKEN':
      return tr('errors.api.invalidToken');
    case 'PASSWORD_CHANGE_REQUIRED':
      return tr('errors.api.passwordChangeRequired');
    case 'ACCESS_DENIED':
    case 'FORBIDDEN':
      return tr(ApiErrorKey.ACCESS_DENIED);
    case 'CALENDAR_PROVIDER_NOT_CONFIGURED':
      // CORA-300 — raced connect against a provider whose OAuth config is
      // absent (the UI normally disables Connect before this can happen).
      return tr('errors.api.calendarProviderNotConfigured');
    case 'TREATMENT_ORDER_NOT_REQUESTED':
      return tr('errors.api.treatmentOrderNotRequested');
    case 'TREATMENT_ORDER_REBOOKED_OR_CLOSED':
      return tr('errors.api.treatmentOrderRebookedOrClosed');
    case 'APPOINTMENT_ALREADY_STARTED':
      return tr('errors.api.appointmentAlreadyStarted');
    case 'APPOINTMENT_SLOT_TAKEN':
      return tr('errors.api.appointmentSlotTaken');
    case 'TRIAGE_ALREADY_STARTED':
      return tr('errors.api.triageAlreadyStarted');
    case 'TRIAGE_START_TAKES_NO_DATA':
      return tr('errors.api.triageStartTakesNoData');
    case 'INTERNAL_ERROR':
      return tr('errors.api.internal');
    default:
      return null;
  }
}

/**
 * The i18n key of the message for a failed request whose flow has its own messages. `specificKeys` maps the flow's
 * domain codes or business rules to their keys; any failure nothing else names (a rejected input, a server error, an
 * unknown code) gets `fallbackKey`.
 */
export function apiErrorKey(err: unknown, fallbackKey: string, specificKeys: Readonly<Record<string, string>> = {}): string {
  if (isConnectionError(err)) return ApiErrorKey.NETWORK;
  const status = (err as AxiosLikeError | null | undefined)?.response?.status;
  const payload = extractPayload(err);
  const code = typeof payload?.code === 'string' ? payload.code : '';
  const rule = typeof payload?.errors?.rule === 'string' ? payload.errors.rule : '';
  const known = ownKey(specificKeys, code) ?? ownKey(specificKeys, rule) ?? ownKey(MISMATCH_KEYS, code);
  if (known) return known;
  if (status === HttpStatus.NOT_FOUND || NOT_FOUND_CODES.includes(code)) return ApiErrorKey.ITEM_UNAVAILABLE;
  if (status === HttpStatus.FORBIDDEN) return ApiErrorKey.ACCESS_DENIED;
  if (status === HttpStatus.TOO_MANY_REQUESTS) return ApiErrorKey.TOO_MANY_REQUESTS;
  // A caller that names no conflict or size key still keeps the meaning of the refusal.
  if (status === HttpStatus.CONFLICT || code === 'CONFLICT') return ApiErrorKey.CONFLICT;
  if (status === HttpStatus.PAYLOAD_TOO_LARGE || code === 'PAYLOAD_TOO_LARGE') return ApiErrorKey.PAYLOAD_TOO_LARGE;
  return fallbackKey;
}

/** The record a not-found answer names (`errors.entity`), or null when the request failed otherwise. */
export function notFoundEntity(err: unknown): string | null {
  const status = (err as AxiosLikeError | null | undefined)?.response?.status;
  const payload = extractPayload(err);
  const code = typeof payload?.code === 'string' ? payload.code : '';
  if (status !== HttpStatus.NOT_FOUND && !NOT_FOUND_CODES.includes(code)) return null;
  const entity = payload?.errors?.entity;
  return typeof entity === 'string' ? entity : null;
}

/** The translated `apiErrorKey` message, for services that answer an `ApiResponse`. */
export function apiErrorMessage(err: unknown, fallbackKey: string, specificKeys: Readonly<Record<string, string>> = {}): string {
  return tr(apiErrorKey(err, fallbackKey, specificKeys));
}

function extractPayload(err: unknown): ApiErrorPayload | null {
  if (!err || typeof err !== 'object') return null;
  const e = err as AxiosLikeError;
  const data = e.response?.data;
  if (!data || typeof data !== 'object') return null;
  return data;
}
